import React, { useState } from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

import ContrastTag from '../../../../../components/shared/ContrastTag';
import { AiPanelTrait } from '../../../../../types/geneCodes/AiPanelReportTypes';
import { getSeverityColor } from './severity';
import { Html, TagRow, text, textList } from './primitives';
import './panelReport.css';

// The platform's placeholder for a missing value — never "N/A", and never "0", which would
// assert a measured zero where the field is simply absent.
const MISSING = '—';

const variantLines = (trait: AiPanelTrait): string[] => {
	const genes = Array.isArray(trait.affected_genes) ? trait.affected_genes : [];
	const lines: string[] = [];
	genes.forEach((gene) => {
		if (!gene || typeof gene !== 'object') return;
		const geneName = text(gene.gene) ?? MISSING;
		const snps = Array.isArray(gene.snps) ? gene.snps : [];
		snps.forEach((snp) => {
			if (!snp || typeof snp !== 'object') return;
			lines.push(
				`${geneName} (${text(snp.rsid) ?? MISSING}: ${text(snp.genotype) ?? MISSING} · ${text(snp.risk_alleles) ?? MISSING} risk alleles)`,
			);
		});
	});
	return lines;
};

// ⚠️ COLLAPSED BY DEFAULT, LIKE gene.codes (temp/gene.codes/js/pubscripts1.js:397).
// A panel routinely carries 13 traits, and this renders in a 400px sidebar. Expanded, the
// section is a page of prose nobody scrolls; collapsed, it is a ranked list of severities.
const TraitCard: React.FC<{ trait: AiPanelTrait; defaultOpen?: boolean }> = ({ trait, defaultOpen = false }) => {
	const [open, setOpen] = useState(defaultOpen);

	const name = text(trait.name);
	const severity = text(trait.genetic_risk_level ?? trait.severity);
	const riskAlleles = text(trait.risk_allele_count);
	const riskScore = text(trait.genetic_risk_percentage);
	const genes = textList((Array.isArray(trait.affected_genes) ? trait.affected_genes : []).map((g) => g?.gene));
	const variants = variantLines(trait);

	return (
		// Flat surface, not a nested antd Card — design-system §10/§24.
		<div className="mg-record-card mg-trait-card" style={{ '--mg-trait-severity': getSeverityColor(severity) } as React.CSSProperties}>
			<div
				role="button"
				tabIndex={0}
				onClick={() => setOpen((value) => !value)}
				onKeyDown={(event) => {
					if (event.key === 'Enter' || event.key === ' ') setOpen((value) => !value);
				}}
				className="mg-trait-card__toggle"
			>
				{open ? <DownOutlined className="mg-trait-card__caret" /> : <RightOutlined className="mg-trait-card__caret" />}
				<span className="mg-trait-card__name">{name ?? 'Unnamed trait'}</span>
				{severity && (
					<ContrastTag background={getSeverityColor(severity)} style={{ fontSize: 'var(--mg-fs-xxs)', margin: 0 }}>
						{severity.toUpperCase()}
					</ContrastTag>
				)}
			</div>

			{(riskAlleles || riskScore) && (
				<div className="mg-trait-card__metrics">
					{riskAlleles && <span>Risk alleles: {riskAlleles}</span>}
					{riskScore && <span>Risk score: {riskScore}%</span>}
				</div>
			)}

			{open && (
				<div className="mg-trait-card__detail">
					{genes.length > 0 && (
						<div className="mg-record-row">
							<span>Genes affected: </span>
							<TagRow items={genes} />
						</div>
					)}
					{variants.length > 0 && (
						<div className="mg-record-row">
							<span>Key variants:</span>
							<ul className="mg-trait-card__variants">
								{variants.map((line, index) => (
									<li key={index}>{line}</li>
								))}
							</ul>
						</div>
					)}
					<Html value={trait.analysis} />
					{text(trait.gene_function_impact) && (
						<>
							<div className="mg-trait-card__eyebrow">Gene function impact</div>
							<Html value={trait.gene_function_impact} />
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default TraitCard;
